import { PropsWithChildren, ReactNode } from 'react';

import { Avatar } from '@opendoor/bricks/complex';
import { Box, Flex, Link, Text } from '@opendoor/bricks/core';
import { IconExternalLink } from '@tabler/icons-react';

import SocialIcon from 'components/shared/icons/SocialIcon';

import { IAuthor, IEditor, IReviewer } from '../../declarations/contentful';
import { getSocialLink } from '../../helpers/socialLinks';
import typography from './typography';

// Person component, with a place for extra text (i.e. passed in as children)
const PersonInline: React.FC<PropsWithChildren<{ person?: IAuthor | IEditor | IReviewer }>> = (
  props,
) => {
  if (!props.person) {
    return null;
  }
  const {
    firstName = '',
    lastName = '',
    picture = '',
    credential,
    displayName,
  } = props.person.fields;
  const initials = firstName.charAt(0) + lastName.charAt(0);
  return (
    <Flex key={firstName + lastName} alignSelf="flex-start" alignItems="center">
      {picture && (
        <Box display={['none', null, null, 'initial']}>
          <Avatar size="lg" src={picture} initials={initials} />
        </Box>
      )}
      <Box marginLeft={['0', null, null, picture ? '4' : '0']} marginBottom="0">
        <Text {...typography.authorName} marginBottom={0} mt="0">
          {displayName}
          {credential && (
            <>
              <Text as="span" {...typography.authorName}>
                ,&nbsp;
              </Text>
              <Text as="i" {...typography.authorName} fontWeight="regular" margin="0">
                {credential}
              </Text>
            </>
          )}
        </Text>
        {props.children}
      </Box>
    </Flex>
  );
};

const PersonWithBio: React.FC<{ person: IAuthor | IEditor | IReviewer; roleText: string }> = (
  props,
) => {
  const {
    firstName = '',
    lastName = '',
    picture = '',
    displayName,
    bio = '',
    website = '',
    twitterUsername = '',
    instagramUsername = '',
    linkedInUsername = '',
  } = props.person.fields;
  const initials = firstName.charAt(0) + lastName.charAt(0);
  const renderLink = (url: string, iconComponent: ReactNode) => (
    <Link
      aria-label=""
      href={url}
      target="_blank"
      analyticsName=""
      display="flex"
      alignItems="center"
      opacity="0.2"
      _hover={{ opacity: '1.0' }}
    >
      {iconComponent}
    </Link>
  );

  return (
    <Flex key={firstName + lastName} flexDirection="column" marginBottom={4}>
      <Avatar size="lg" src={picture} initials={initials} />
      <Flex marginTop={4} flexWrap="wrap" gridColumnGap="4">
        <Box>
          <Text as="span" {...typography.authorName}>
            {displayName}
          </Text>
          <Text as="span" {...typography.authorName}>
            ,&nbsp;
          </Text>
          <Text as="i" {...typography.authorName} fontWeight="regular">
            {props.roleText}
          </Text>
        </Box>
        <Flex gridGap="4" display="inline-flex" marginBottom={2}>
          {twitterUsername &&
            renderLink(
              getSocialLink('twitter', twitterUsername),
              <SocialIcon socialPlatform="twitter" />,
            )}
          {instagramUsername &&
            renderLink(
              getSocialLink('instagram', instagramUsername),
              <SocialIcon socialPlatform="instagram" />,
            )}
          {linkedInUsername &&
            renderLink(
              getSocialLink('linkedin', linkedInUsername),
              <SocialIcon socialPlatform="linkedin" />,
            )}
          {website && renderLink(website, <IconExternalLink size={16} />)}
        </Flex>
      </Flex>
      <Text size="s1" marginTop={0}>
        {bio}
      </Text>
    </Flex>
  );
};

export { PersonInline, PersonWithBio };
